import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";

/**
 * A pad to sign on with a mouse, a finger or a pen. One component for every
 * page that takes a signature; the options are where the pages differ:
 *
 *   resolution  "device": the canvas matches its on-screen size times the
 *               screen's pixel ratio (sharp on phones), re-made when that
 *               size changes — only then, so a phone's URL bar hiding on
 *               scroll does not wipe a signature. Or `{ width, height }`: a
 *               fixed drawing size, scaled by CSS.
 *   background  "white": painted white (the signed SOA crops ink against
 *               white). "transparent": left clear, so the PNG sits on a PDF
 *               without a box (the PDF-signing page).
 *
 * The ref offers clear(), isEmpty(), toDataURL(type), toBlob(type, quality)
 * and cropToInk(type, quality) — the signature cut to its ink plus a margin.
 * `onChange(hasSignature)` fires when the pad goes from empty to signed and
 * back.
 */
const SignaturePad = forwardRef(function SignaturePad(
  { resolution = "device", background = "white", strokeStyle = "#111", lineWidth = 2, className = "", style, ariaLabel, onChange },
  ref,
) {
  // Compared as numbers: callers pass `{ width, height }` inline, a new object
  // every render, and treating that as a new size would wipe the signature.
  const device = resolution === "device";
  const fixedWidth = device ? 0 : resolution.width;
  const fixedHeight = device ? 0 : resolution.height;
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const signed = useRef(false);
  const size = useRef({ width: 0, height: 0 });
  const changeRef = useRef(onChange);
  useEffect(() => {
    changeRef.current = onChange;
  });

  const setSigned = useCallback((value) => {
    if (signed.current === value) return;
    signed.current = value;
    changeRef.current?.(value);
  }, []);

  const paintBackground = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (background === "white") {
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.restore();
  }, [background]);

  // Sizes the drawing surface and sets the pen. Wipes the canvas.
  const setup = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (device) {
      const ratio = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (!width || !height || (width === size.current.width && height === size.current.height)) return;
      size.current = { width, height };
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    } else {
      canvas.width = fixedWidth;
      canvas.height = fixedHeight;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    ctx.lineWidth = lineWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = strokeStyle;
    paintBackground();
    setSigned(false);
  }, [device, fixedWidth, fixedHeight, lineWidth, strokeStyle, paintBackground, setSigned]);

  useEffect(() => {
    setup();
    if (!device || typeof ResizeObserver !== "function") return undefined;
    const observer = new ResizeObserver(() => setup());
    observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, [setup, device]);

  // A point in drawing units: CSS pixels in "device" mode (the context is
  // scaled), canvas pixels for a fixed size.
  const pointOf = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    if (device) return { x, y };
    return { x: (x / rect.width) * canvas.width, y: (y / rect.height) * canvas.height };
  };

  const onPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    canvasRef.current.setPointerCapture?.(event.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pointOf(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const onPointerMove = (event) => {
    if (!drawing.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pointOf(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    setSigned(true);
  };

  const endStroke = () => {
    drawing.current = false;
  };

  useImperativeHandle(ref, () => ({
    clear() {
      paintBackground();
      setSigned(false);
    },
    isEmpty: () => !signed.current,
    toDataURL: (type = "image/png") => canvasRef.current.toDataURL(type),
    toBlob: (type = "image/png", quality) => new Promise((resolve) => canvasRef.current.toBlob(resolve, type, quality)),
    /** The signature cut to its ink with a 10px margin; null when nothing is drawn. */
    cropToInk(type = "image/png", quality = 1) {
      const canvas = canvasRef.current;
      const w = canvas.width;
      const h = canvas.height;
      const data = canvas.getContext("2d").getImageData(0, 0, w, h).data;
      let minX = w;
      let minY = h;
      let maxX = 0;
      let maxY = 0;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4;
          const ink = background === "white" ? data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250 : data[i + 3] > 0;
          if (ink) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (minX >= maxX || minY >= maxY) return Promise.resolve(null);
      const pad = 10;
      minX = Math.max(0, minX - pad);
      minY = Math.max(0, minY - pad);
      maxX = Math.min(w - 1, maxX + pad);
      maxY = Math.min(h - 1, maxY + pad);
      const cw = maxX - minX + 1;
      const ch = maxY - minY + 1;
      const off = document.createElement("canvas");
      off.width = cw;
      off.height = ch;
      off.getContext("2d").drawImage(canvas, minX, minY, cw, ch, 0, 0, cw, ch);
      return new Promise((resolve) => off.toBlob(resolve, type, quality));
    },
  }));

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ touchAction: "none", ...style }}
      role="img"
      aria-label={ariaLabel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endStroke}
      onPointerCancel={endStroke}
      onPointerLeave={endStroke}
    />
  );
});

export default SignaturePad;
